import React, { useEffect, useState } from 'react';
import {
  Box, Chip, CircularProgress, IconButton, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { DashboardLayout } from '@components/DashboardLayout.tsx';
import { PageCard } from '@components/common/PageCard.tsx';
import { PageToolbar } from '@components/common/PageToolbar.tsx';
import { UniversalPagination } from '@components/common/UniversalPagination.tsx';
import { toastService } from '@services/toast.service.ts';
import {
  translationAuditService,
  MissingTranslationItem,
  MissingTranslationSource,
} from '@services/translation-audit.service.ts';

const SOURCE_LABEL: Record<MissingTranslationSource, string> = {
  food: 'Їжа',
  workout: 'Тренування (v1)',
  workout_v2: 'Тренування (v2)',
  medication: 'Ліки',
};

const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
const formatDate = (iso: string): string =>
  new Date(iso).toLocaleString('uk-UA', { timeZone: userTimeZone });

export const DevMissingTranslationsPage: React.FC = () => {
  const [items, setItems] = useState<MissingTranslationItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sourceFilter, setSourceFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  const load = async () => {
    setIsLoading(true);
    try {
      const { items } = await translationAuditService.getMissingUkTranslations();
      setItems(items);
    } catch {
      toastService.error('Не вдалося завантажити список позицій без перекладу.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = sourceFilter ? items.filter((i) => i.type === sourceFilter) : items;
  const pageCount = Math.max(1, Math.ceil(filtered.length / limit));
  const pageItems = filtered.slice((page - 1) * limit, page * limit);

  return (
    <DashboardLayout>
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Живий аудит довідників (їжа, тренування, ліки) — позиції, де немає українського перекладу назви
          (`nameI18n.uk` порожній). Без окремої таблиці в БД — сканується напряму при відкритті сторінки.
        </Typography>
        <PageCard>
          <PageToolbar
            storageKey="filters:dev-missing-translations"
            filters={[
              {
                id: 'source',
                type: 'select',
                label: 'Довідник',
                value: sourceFilter,
                onChange: (v) => { setSourceFilter(v); setPage(1); },
                options: [
                  { value: '', label: 'Всі' },
                  ...Object.entries(SOURCE_LABEL).map(([value, label]) => ({ value, label })),
                ],
              },
            ]}
            onReset={() => setSourceFilter('')}
            extraActions={
              <Tooltip title="Оновити">
                <IconButton onClick={load} disabled={isLoading}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            }
          />

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : filtered.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              Нічого не знайдено — у всіх позиціях є український переклад.
            </Typography>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Довідник</TableCell>
                      <TableCell>ID</TableCell>
                      <TableCell>Назва (RU)</TableCell>
                      <TableCell>Назва (EN)</TableCell>
                      <TableCell>Створено</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pageItems.map((item) => (
                      <TableRow key={`${item.type}-${item.id}`} hover>
                        <TableCell><Chip label={SOURCE_LABEL[item.type]} size="small" variant="outlined" /></TableCell>
                        <TableCell>#{item.id}</TableCell>
                        <TableCell>{item.nameRu || '—'}</TableCell>
                        <TableCell>{item.nameEn || '—'}</TableCell>
                        <TableCell>{formatDate(item.createdAt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <UniversalPagination
                page={page}
                count={pageCount}
                limit={limit}
                total={filtered.length}
                onPageChange={setPage}
                onLimitChange={(l) => { setLimit(l); setPage(1); }}
                storageKey="pagination:dev-missing-translations"
              />
            </>
          )}
        </PageCard>
      </Box>
    </DashboardLayout>
  );
};
